import { useCart, type CartLine } from '../../store/cart';
import { askManagerPassword } from '../../store/managerPrompt';
import { trNow } from '../../lib/language';

/**
 * The rules for EDITING a held order reopened in the cart (asked 2026-09-21):
 *
 *   - adding items, or raising a quantity, is always allowed — and those
 *     additions can be sent to the kitchen on their own ("ADDED ITEMS");
 *   - REMOVING a line, or lowering a quantity below what the held order had,
 *     needs the manager's password (LoginName 'Admin'). Asked once per edit;
 *     the server checks it again on save, so the till cannot be bypassed.
 */

/** Would setting `line` to `newQty` remove something the held order had? */
export function isHeldRemoval(line: CartLine, newQty: number): boolean {
  const s = useCart.getState();
  return s.editingHeld && line.heldBaseQty != null && newQty < line.heldBaseQty - 1e-9;
}

/**
 * Make sure the manager has approved removals in this edit. Resolves true
 * when approved (now or earlier in the same edit), false when the cashier
 * backed out.
 */
export async function ensureManagerApproval(): Promise<boolean> {
  if (useCart.getState().heldManagerPassword) return true;
  const password = await askManagerPassword({
    en: 'Removing items from a held order needs the manager’s approval.',
    ar: 'حذف أصناف من طلب معلق يتطلب موافقة المدير.',
  });
  if (!password) return false;
  useCart.getState().setHeldManagerPassword(password);
  return true;
}

/** Units ADDED since the order was held — new lines plus raised quantities. */
export function heldAdditionCount(): number {
  const s = useCart.getState();
  if (!s.editingHeld) return 0;
  let n = 0;
  for (const l of s.lines) {
    if (l.isReturn) continue;
    const base = l.heldBaseQty ?? 0;
    if (l.qty > base + 1e-9) n += l.qty - base;
  }
  return Math.round(n * 1000) / 1000;
}

/**
 * The extras a held re-hold carries: the manager's password when one was
 * given, and whether to print the added items — asked here, once, when there
 * are any.
 */
export function heldSaveExtras(): { manager_password?: string; print_new_items: boolean } {
  const s = useCart.getState();
  const added = heldAdditionCount();
  const print =
    added > 0 &&
    window.confirm(
      trNow({
        en: `Print the ${added} new item(s) to the kitchen?\n\nOK = print only the new items · Cancel = don't print`,
        ar: `طباعة ${added} صنف جديد في المطبخ؟\n\nموافق = طباعة الأصناف الجديدة فقط · إلغاء = بدون طباعة`,
      }),
    );
  return {
    ...(s.heldManagerPassword ? { manager_password: s.heldManagerPassword } : {}),
    print_new_items: print,
  };
}

/**
 * The held-order / table refusals, in the session's language. The server
 * sends English sentences; the till says them in the cashier's language by
 * their CODE. Null for anything else (the caller keeps the server's text).
 */
export function heldErrorText(err: unknown): string | null {
  const data = (err as { response?: { data?: { code?: string; details?: { held_entry_no?: number } } } })
    ?.response?.data;
  const n = data?.details?.held_entry_no;
  switch (data?.code) {
    case 'TABLE_OCCUPIED':
      return trNow({
        en: `This table is taken by held order #${n ?? ''}. Open that order from the table, or pick another table.`,
        ar: `هذه الطاولة محجوزة للطلب المعلق #${n ?? ''}. افتح ذلك الطلب من الطاولة أو اختر طاولة أخرى.`,
      });
    case 'MANAGER_APPROVAL_REQUIRED':
      return trNow({
        en: "Removing items from a held order needs the manager's password.",
        ar: 'حذف أصناف من طلب معلق يتطلب كلمة مرور المدير.',
      });
    case 'MANAGER_PASSWORD_INVALID':
      return trNow({ en: 'Wrong manager password.', ar: 'كلمة مرور المدير غير صحيحة.' });
    case 'HELD_ORDER_NOT_FOUND':
      return trNow({
        en: 'This held order was paid or cancelled a moment ago.',
        ar: 'تم دفع هذا الطلب المعلق أو إلغاؤه قبل لحظات.',
      });
    case 'ALREADY_PAID':
      return trNow({ en: 'Somebody else paid this order a moment ago.', ar: 'قام شخص آخر بدفع هذا الطلب قبل لحظات.' });
    case 'PAYMENT_TYPE_NOT_ALLOWED':
      return trNow({
        en: 'Pay a held order with a real payment method — it is already unpaid.',
        ar: 'ادفع الطلب المعلق بطريقة دفع فعلية — فهو غير مدفوع بالفعل.',
      });
    case 'GIFT_CARD_NOT_SUPPORTED':
      return trNow({
        en: 'A held order cannot be paid with a gift card.',
        ar: 'لا يمكن دفع طلب معلق ببطاقة هدية.',
      });
    case 'ORDER_NOT_HOLDABLE':
      return trNow({
        en: 'This order cannot be held (returns, gift cards, coupons, points, delivery and membership are not held).',
        ar: 'لا يمكن تعليق هذا الطلب (لا تُعلّق المرتجعات وبطاقات الهدايا والكوبونات والنقاط والتوصيل والعضوية).',
      });
    default:
      return null;
  }
}
